import { shallowEqual } from 'react-redux';

import { DetailedIconVirtualTour } from '@zillow/constellation';
import { Box, Button, Card, Heading, Image, Text } from '@zillow/constellation-10';

import { useSelector } from '../../../../../selectors/useSelector';

import AppDownload from '../../../../../images/AppDownload.svg';
import TourAddress from '../../../../../images/TourAddress.svg';

type ActionCardProps = {
  detailedIcon?: JSX.Element;
  iconSrc?: string;
  iconAlt?: string;
  title: string;
  description: string;
  cta: string;
  onClick: () => void;
};

const ActionCard = ({ detailedIcon, iconSrc, iconAlt, title, description, cta, onClick }: ActionCardProps) => (
  <Card tone="neutral" css={{ display: 'flex', flexDirection: { base: 'column', md: 'row' }, alignItems: 'center' }}>
    {iconSrc ? <Image src={iconSrc} alt={iconAlt} /> : null}
    {detailedIcon ? detailedIcon : null}
    <Box
      css={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        padding: { base: '{spacing.400} 0', md: '0 {spacing.400}' },
        textAlign: { base: 'center', md: 'left' },
      }}
    >
      <Text textStyle="body-sm-bold" css={{ color: 'blue.400' }}>
        {title}
      </Text>
      <Text>{description}</Text>
    </Box>
    <Button
      tone="brand"
      emphasis="outlined"
      onClick={onClick}
      fluid
      css={{
        flexShrink: 0,
        md: {
          width: 'auto',
        },
      }}
    >
      {cta}
    </Button>
  </Card>
);

const EmptyTours = () => {
  const { isMultifamily = false } = useSelector(
    (state) => ({
      isMultifamily: state?.properties?.currentProperty?.isMultifamily,
    }),
    shallowEqual,
  );

  const handleDownloadAppClick = () => {
    window.open('https://www.zillow.com/marketing/3d-home/', '_blank');
  };

  const handleVirtualTourClick = () => {
    window.open('https://showingtimeplus.com/solutions/zillow-media-experts/', '_blank');
  };

  return (
    <Box css={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Box css={{ paddingBlock: { base: '200', md: '400' } }}>
        <Box css={{ width: '100%', maxWidth: '440px', textAlign: 'center' }}>
          <Heading level={3}>Show off your property with a 3D tour</Heading>
        </Box>
      </Box>

      {window.innerHeight > 800 && (
        <Box css={{ paddingBlock: { base: '200', md: '400' } }}>
          <Image src={TourAddress} alt="Tour address" />
        </Box>
      )}

      <Box css={{ paddingBlock: { base: '200', md: '400' } }}>
        <ActionCard
          iconSrc={AppDownload}
          iconAlt="App Download"
          title="Create a tour"
          description="Create a tour for free with the Zillow 3D Home app"
          cta="Download app"
          onClick={handleDownloadAppClick}
        />
      </Box>

      {isMultifamily && (
        <Box css={{ paddingBlock: { base: '200', md: '400' } }}>
          <ActionCard
            detailedIcon={<DetailedIconVirtualTour title="Virtual Tour" />}
            title="Hire a photographer"
            description="Impress renters with professionally shot 3D tours"
            cta="Explore options"
            onClick={handleVirtualTourClick}
          />
        </Box>
      )}
    </Box>
  );
};

export default EmptyTours;
